import Fuse, { type FuseSearchOptions, type IFuseOptions } from "fuse.js";
import { useCallback, useMemo } from "react";

interface PriorityRule {
  searchText: string;
  resultName: string;
}

interface Props<T extends object> {
  data: T[];
  options?: IFuseOptions<T>;
  priorityMapping?: PriorityRule[];
  priorityDataKey?: keyof T;
  enablePrioritization?: boolean;
}

const fuseDefaultOptions = {
  shouldSort: true,
};

export const useFuse = <T extends object>({
  data,
  options,
  priorityMapping = [],
  priorityDataKey,
  enablePrioritization = false,
}: Props<T>) => {
  const fuse = useMemo(() => new Fuse(data, options || fuseDefaultOptions), [data, options]);

  const prioritizeSearchResult = useCallback(
    (searchResults: T[], searchText: string) => {
      if (!enablePrioritization || !priorityMapping.length) return searchResults;

      const matchingRule = priorityMapping.find((rule) => rule.searchText.toLowerCase() === searchText.toLowerCase());

      if (matchingRule && priorityDataKey) {
        const priorityIndex = searchResults.findIndex(
          (result) => String(result[priorityDataKey]).toLowerCase() === matchingRule.resultName.toLowerCase(),
        );

        if (priorityIndex !== -1) {
          const [priorityResult] = searchResults.splice(priorityIndex, 1);
          return [priorityResult, ...searchResults];
        }
      }
      return searchResults;
    },
    [priorityMapping, priorityDataKey, enablePrioritization],
  );

  const search = useCallback(
    (searchText: string, opts?: FuseSearchOptions) => {
      if (!searchText) return data;
      const results = fuse.search(searchText, opts).map((result) => result.item);
      return prioritizeSearchResult(results, searchText);
    },
    [fuse, data, prioritizeSearchResult],
  );

  return {
    fuse,
    search,
  };
};
